import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Upload, FileSpreadsheet, CheckCircle, Download, AlertCircle, X } from 'lucide-react';
import { useZymplerImport } from '@/hooks/useZymplerImport';
import { ZymplerPreviewTable } from './ZymplerPreviewTable';
import { BusinesscaseRevenue } from '@/types/businesscase';
import { Progress } from '@/components/ui/progress';

interface ZymplerImportModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  businesscaseId: string;
  existingRevenues: BusinesscaseRevenue[];
  onImportComplete: () => Promise<void>;
}

export function ZymplerImportModal({ 
  open,
  onOpenChange,
  businesscaseId, 
  existingRevenues, 
  onImportComplete 
}: ZymplerImportModalProps) {
  const {
    importData,
    isProcessing,
    handleFileUpload,
    updatePreviewRow,
    executeImport,
    downloadCleaned,
    hasData,
  } = useZymplerImport(businesscaseId, existingRevenues);

  const [isDragging, setIsDragging] = useState(false);

  const handleFile = async (file: File) => {
    await handleFileUpload(file);
  };

  const handleInputChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    await handleFile(file);
    e.target.value = ''; // Reset input
  };

  const handleConfirmImport = async () => {
    const success = await executeImport();
    if (success) {
      await onImportComplete();
      onOpenChange(false); // Close modal after successful import
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = async (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    
    const file = e.dataTransfer.files[0];
    if (file && (file.name.endsWith('.xlsx') || file.name.endsWith('.xls') || file.name.endsWith('.csv'))) {
      await handleFile(file);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-primary">
            <FileSpreadsheet className="w-5 h-5" />
            Zympler Export Importeren
          </DialogTitle>
          <DialogDescription>
            Upload uw Zympler export (.xlsx of .csv) om automatisch de opbrengsten te vullen
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Upload Section */}
          {!hasData && (
            <div 
              className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
                isDragging 
                  ? 'border-primary bg-primary/5' 
                  : 'border-primary/30 bg-background'
              }`}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
            >
              <Upload className={`w-12 h-12 mx-auto mb-4 transition-colors ${
                isDragging ? 'text-primary' : 'text-primary/50'
              }`} />
              <p className="text-sm text-muted-foreground mb-4">
                {isDragging 
                  ? 'Drop het bestand hier...' 
                  : 'Sleep uw UitdraaiZympler.xlsx bestand hier of klik om te uploaden'
                }
              </p>
              <label>
                <Button 
                  variant="default"
                  disabled={isProcessing}
                  asChild
                >
                  <span>
                    <Upload className="w-4 h-4 mr-2" />
                    Selecteer Bestand
                  </span>
                </Button>
                <input
                  type="file"
                  accept=".xlsx,.xls,.csv"
                  onChange={handleInputChange}
                  className="hidden"
                  disabled={isProcessing}
                />
              </label>
            </div>
          )}

          {/* Processing Indicator */}
          {isProcessing && !hasData && (
            <div className="space-y-2">
              <p className="text-sm text-center text-muted-foreground">
                Processing Zympler file...
              </p>
              <Progress value={undefined} className="w-full" />
            </div>
          )}

          {/* Preview & Summary */}
          {hasData && importData && (
            <div className="space-y-4">
              {/* Import Summary */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Card className="bg-background">
                  <CardContent className="pt-6">
                    <div className="text-2xl font-bold text-primary">
                      {importData.totalRows}
                    </div>
                    <p className="text-xs text-muted-foreground">Jaren gevonden</p>
                  </CardContent>
                </Card>
                
                <Card className="bg-background">
                  <CardContent className="pt-6">
                    <div className="text-2xl font-bold text-green-600">
                      {importData.mappedColumns.length}
                    </div>
                    <p className="text-xs text-muted-foreground">Kolommen gemapped</p>
                  </CardContent>
                </Card>
                
                <Card className="bg-background">
                  <CardContent className="pt-6">
                    <div className="text-2xl font-bold text-orange-500">
                      {importData.unmappedColumns.length}
                    </div>
                    <p className="text-xs text-muted-foreground">Kolommen overgeslagen</p>
                  </CardContent>
                </Card>
              </div>

              {/* Warnings */}
              {importData.warnings.length > 0 && (
                <div className="bg-orange-50 dark:bg-orange-950 border border-orange-200 dark:border-orange-800 rounded-lg p-4">
                  <div className="flex items-start gap-2">
                    <AlertCircle className="w-5 h-5 text-orange-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="font-semibold text-sm text-orange-800 dark:text-orange-200">Waarschuwingen:</p>
                      <ul className="text-xs text-orange-700 dark:text-orange-300 list-disc list-inside mt-1">
                        {importData.warnings.map((w, i) => (
                          <li key={i}>{w}</li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              )}

              {/* Unmapped Columns Info */}
              {importData.unmappedColumns.length > 0 && (
                <div className="bg-blue-50 dark:bg-blue-950 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
                  <p className="text-sm text-blue-800 dark:text-blue-200">
                    <strong>Niet-herkende kolommen:</strong> {importData.unmappedColumns.join(', ')}
                  </p>
                  <p className="text-xs text-blue-600 dark:text-blue-400 mt-1">
                    Deze kolommen worden overgeslagen. Neem contact op als deze belangrijk zijn.
                  </p>
                </div>
              )}

              {/* Preview Table */}
              <ZymplerPreviewTable 
                data={importData.parsedData}
                onUpdateRow={updatePreviewRow}
              />

              {/* Action Buttons */}
              <div className="flex flex-col md:flex-row gap-2 justify-end">
                <Button
                  variant="outline"
                  onClick={() => onOpenChange(false)}
                  disabled={isProcessing}
                >
                  <X className="w-4 h-4 mr-2" />
                  Annuleren
                </Button>
                
                <Button
                  variant="outline"
                  onClick={downloadCleaned}
                  disabled={isProcessing}
                >
                  <Download className="w-4 h-4 mr-2" />
                  Download Cleaned
                </Button>
                
                <Button
                  variant="default"
                  onClick={handleConfirmImport}
                  disabled={isProcessing}
                >
                  <CheckCircle className="w-4 h-4 mr-2" />
                  Importeren Bevestigen
                </Button>
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
